import { MenuPlacement } from "react-select";
import styled from "styled-components";
import { MouseEvent } from "react";

import { Option, StyledSelect } from "@/components/Forms/Dropdown";
import Tag, { TagsHolder } from "../Tag";
import { FilterGroupTitle } from "./Parts";
import Text from "../Text";
import { modalRoot } from "../Modal";

const Select = styled(StyledSelect)`
  .react-select__control {
    cursor: pointer;
    height: 40px;
    font-size: 12px;
  }
  .react-select__placeholder {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.black};
  }
`;

const StyledTagsHolder = styled(TagsHolder)`
  margin-top: 12px;
`;

const Wrap = styled.div`
  margin-top: 16px;
`;

const Group = styled.div`
  padding: 16px;
`;

const StyledFilterGroupTitle = styled(FilterGroupTitle)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const ClearButton = styled(Text)`
  transition: 0.2s ease-out color;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

type ChangeCallback<T> = (oldValues: T[]) => T[];

interface Props<OptionValueT> {
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  options: Option<OptionValueT>[];
  menuPlacement?: MenuPlacement;
  label: string;
  setValues: (arg: OptionValueT[] | ChangeCallback<OptionValueT>) => void;
  values: OptionValueT[];
  className?: string;
}

const FilterMultiselect = <OptionValueT extends unknown>({
  disabled = false,
  placeholder = "Select",
  id,
  label,
  options,
  menuPlacement = "bottom",
  setValues,
  className,
  values,
}: Props<OptionValueT>) => {
  const onSelectChange = (val: OptionValueT) => {
    setValues((oldValues) => [...oldValues, val]);
  };

  const onRemoveValue = (removableValue: OptionValueT) => {
    setValues((oldValues) =>
      oldValues.filter((val: OptionValueT) => val !== removableValue)
    );
  };

  const onClear = () => setValues([]);

  const filteredOptions = options.filter(
    (option) => !values.includes(option.value)
  );

  return (
    <Group
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      className={className}
    >
      <StyledFilterGroupTitle>
        {label}
        <ClearButton
          as="button"
          $color="gray"
          $type="extraSmallSemiBold"
          onClick={onClear}
        >
          Clear
        </ClearButton>
      </StyledFilterGroupTitle>
      <Wrap>
        <Select
          classNamePrefix="react-select"
          isDisabled={disabled}
          options={filteredOptions}
          isMulti
          onChange={(options) =>
            onSelectChange((options as Option<OptionValueT>[])[0].value)
          }
          value={[]}
          id={id}
          menuPlacement={menuPlacement}
          placeholder={placeholder}
          menuPortalTarget={modalRoot}
        />
        {!!(values as Array<OptionValueT>)?.length && (
          <StyledTagsHolder>
            {values.map((value: OptionValueT, index: number) => (
              <Tag key={index} onDelete={() => onRemoveValue(value)}>
                {options.find((opt) => opt.value === value)?.label}
              </Tag>
            ))}
          </StyledTagsHolder>
        )}
      </Wrap>
    </Group>
  );
};

export default FilterMultiselect;
